import React from "react";
import { View, Pressable, StyleSheet } from "react-native";
import CustomText from "../../general/CustomText/CustomText";
import { colors, spacing, radii, typographyScale } from "../../../../styles/design-system";

export interface ChipOption {
  key: string;
  value: string;
  subtitle?: string;
  icon?: React.ReactNode;
}

export interface ChipSelectorProps {
  options: ChipOption[];
  selectedKeys: string[];
  onSelectionChange: (keys: string[]) => void;
  multiSelect?: boolean;
}

function ChipSelector({ options, selectedKeys, onSelectionChange, multiSelect = true }: ChipSelectorProps) {
  const handlePress = (key: string) => {
    if (multiSelect) {
      if (selectedKeys.includes(key)) {
        onSelectionChange(selectedKeys.filter((k) => k !== key));
      } else {
        onSelectionChange([...selectedKeys, key]);
      }
    } else {
      onSelectionChange([key]);
    }
  };

  return (
    <View style={styles.container}>
      {options.map((option) => {
        const isSelected = selectedKeys.includes(option.key);
        return (
          <Pressable
            key={option.key}
            onPress={() => handlePress(option.key)}
            style={({ pressed }) => [
              styles.chip,
              option.subtitle && styles.chipWithSubtitle,
              isSelected && styles.chipSelected,
              pressed && styles.chipPressed,
            ]}>
            {option.icon && <View style={styles.iconContainer}>{option.icon}</View>}
            <View style={option.subtitle ? styles.textContainer : undefined}>
              <CustomText
                text={option.value}
                size="s"
                weight={isSelected ? "medium" : "regular"}
                style={[styles.chipText, isSelected && styles.chipTextSelected]}
                useTranslationText={false}
              />
              {option.subtitle && (
                <CustomText
                  text={option.subtitle}
                  size="xs"
                  style={[styles.chipSubtitle, isSelected && styles.chipSubtitleSelected]}
                  useTranslationText={false}
                />
              )}
            </View>
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.sm,
  },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radii.full,
    backgroundColor: colors.grey[50],
    gap: spacing.xs,
  },
  chipWithSubtitle: {
    paddingVertical: spacing.md,
    borderRadius: radii.md,
  },
  textContainer: {
    flexDirection: "column",
    gap: spacing.xs,
  },
  chipSelected: {
    borderWidth: 1,
    borderColor: colors.success[700],
    backgroundColor: colors.success[50],
  },
  chipPressed: {
    opacity: 0.7,
  },
  iconContainer: {
    width: 16,
    height: 16,
    justifyContent: "center",
    alignItems: "center",
  },
  chipText: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  chipTextSelected: {
    color: colors.success[700],
  },
  chipSubtitle: {
    color: colors.grey[500],
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
  },
  chipSubtitleSelected: {
    color: colors.success[600],
  },
});

export default ChipSelector;
